import React from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import {
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
} from "../../store/api/onboardingApi";
import { STEPS, PREPARING_PATH } from "./steps";
import Stepper from "./Stepper";
// The rail is dark, so this is the white lockup. Preparing.jsx is light and
// imports the -dark.svg variant instead.
import logo from "../../assets/maxelpay-logo-light.svg";
import "./onboarding.css";

/**
 * Two-panel onboarding shell (redesign 2026-06-23). A persistent dark brand rail
 * holds the logo, a reassuring headline, the vertical stepper, and a reassurance
 * card + "Save & exit" escape; the active step renders in the light form panel.
 * The global app nav was removed so merchants stay in setup. Deliberately does
 * NOT mount the main Sidebar/Header. Presentation only — step logic unchanged.
 */
const OnboardingLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: state } = useGetOnboardingQuery();
  const [updateOnboarding, { isLoading: exiting }] = useUpdateOnboardingMutation();

  /*
   * "Save & exit" is a decision to leave setup, not a pause. It records that on
   * the merchant's account so the wizard is not put back in front of them at the
   * next login -- previously nothing was written, so resume sent them straight
   * back to the step they had just walked away from.
   *
   * The navigation happens whether or not the write succeeds. Trapping someone
   * in the wizard because a request failed would be a worse bug than the one
   * this fixes, and the client has already saved the same flag locally.
   */
  const handleExit = async () => {
    try {
      await updateOnboarding({ onboarding_dismissed: true }).unwrap();
    } catch {
      /* ignored on purpose -- see above */
    }
    navigate(PREPARING_PATH);
  };

  // Verify and Test payment still have routes but are no longer wizard steps,
  // so a path outside the three resolves to no key. Stepper reads that as
  // "past the wizard" rather than defaulting to step 1.
  const currentKey =
    STEPS.find((s) => location.pathname.startsWith(s.path))?.key || null;

  return (
    <div className="ob-shell">
      <aside className="ob-rail">
        <Link to="/dashboard" className="ob-logo" aria-label="MaxelPay">
          {/* <span className="ob-logo-mark">M</span>
          <span className="ob-logo-word">
            Maxel<span className="ob-logo-pay">Pay</span>
          </span> */}
          <img
            src="/images/maxelpay-Logo.svg"
            alt="MaxelPay"
            className="ob-logo-image"
          />
        </Link>

        <div className="ob-rail-headline">
          <h2>Let&rsquo;s get you accepting crypto</h2>
          <p>
            A few quick steps. You can change any of this later in settings.
          </p>
        </div>

        <Stepper currentKey={currentKey} state={state} />

        <div className="ob-rail-foot">
          <div className="ob-reassure">
            {/* <span className="ob-reassure-icon" aria-hidden="true">
              <Zap size={17} />
            </span> */}
            {/* <div>
              <strong>Live within 24 hours</strong>
              <span>Most merchants finish setup in minutes.</span>
            </div> */}
            <div>
              <strong>Need help?</strong>
              <span>
                Email us:{" "}
                <a href="mailto:support@maxelpay.com">support@maxelpay.com</a>
              </span>
            </div>
          </div>
          {/* Steps save as they are completed, so this only records the exit
              itself. It goes via the preparing screen so leaving looks the same
              as finishing. */}
          <button type="button" className="ob-exit" onClick={handleExit} disabled={exiting}>
            {exiting ? "Saving…" : <>Save &amp; exit</>}
          </button>
        </div>
      </aside>

      <main className="ob-panel">
        <Outlet />
      </main>
    </div>
  );
};

export default OnboardingLayout;
